import type { AppearanceTheme, ResolvedAppearanceMode } from "./types";

const MS_PER_MINUTE = 60 * 1000;

export type SolarPhase = "night" | "pre-dawn" | "sunrise" | "day" | "sunset" | "post-sunset";

export type SolarCyclePhaseConfig = {
  phase: SolarPhase;
  theme: AppearanceTheme;
  mode: ResolvedAppearanceMode;
  label: string;
};

/** Minutes before and after each phase boundary to crossfade palettes. */
export const SOLAR_CYCLE_BLEND_HALF_MINUTES = 15;

export const SOLAR_CYCLE_BLEND_DURATION_MINUTES = SOLAR_CYCLE_BLEND_HALF_MINUTES * 2;

export const SOLAR_CYCLE_CLOCK_ORDER: SolarPhase[] = [
  "pre-dawn",
  "sunrise",
  "day",
  "sunset",
  "post-sunset",
  "night",
];

export const SOLAR_CYCLE_PHASES: SolarCyclePhaseConfig[] = [
  { phase: "night", theme: "noir", mode: "dark", label: "Night" },
  { phase: "pre-dawn", theme: "slate", mode: "dark", label: "Pre-dawn" },
  { phase: "sunrise", theme: "magic-hour", mode: "light", label: "Sunrise" },
  { phase: "day", theme: "magic-hour", mode: "light", label: "Day" },
  { phase: "sunset", theme: "tungsten", mode: "light", label: "Sunset" },
  { phase: "post-sunset", theme: "velvet", mode: "dark", label: "Blue hour" },
];

const PHASE_BY_ID = Object.fromEntries(
  SOLAR_CYCLE_PHASES.map((config) => [config.phase, config])
) as Record<SolarPhase, SolarCyclePhaseConfig>;

type ClockPhaseWindow = {
  phase: SolarPhase;
  startMinutes: number;
  endMinutes: number;
};

type PhaseTransition = {
  atMinutes: number;
  from: SolarPhase;
  to: SolarPhase;
};

/** Local-time phase bands for Solar Cycle. */
const CLOCK_PHASE_WINDOWS: ClockPhaseWindow[] = [
  { phase: "pre-dawn", startMinutes: 5 * 60, endMinutes: 6 * 60 + 30 },
  { phase: "sunrise", startMinutes: 6 * 60 + 30, endMinutes: 8 * 60 },
  { phase: "day", startMinutes: 8 * 60, endMinutes: 17 * 60 },
  { phase: "sunset", startMinutes: 17 * 60, endMinutes: 19 * 60 },
  { phase: "post-sunset", startMinutes: 19 * 60, endMinutes: 21 * 60 },
];

const PHASE_TRANSITIONS: PhaseTransition[] = [
  { atMinutes: 5 * 60, from: "night", to: "pre-dawn" },
  { atMinutes: 6 * 60 + 30, from: "pre-dawn", to: "sunrise" },
  { atMinutes: 8 * 60, from: "sunrise", to: "day" },
  { atMinutes: 17 * 60, from: "day", to: "sunset" },
  { atMinutes: 19 * 60, from: "sunset", to: "post-sunset" },
  { atMinutes: 21 * 60, from: "post-sunset", to: "night" },
];

export type SolarCycleBlendEndpoint = {
  phase: SolarPhase;
  theme: AppearanceTheme;
  mode: ResolvedAppearanceMode;
};

export type SolarCycleBlend = {
  from: SolarCycleBlendEndpoint;
  to: SolarCycleBlendEndpoint;
  progress: number;
};

export type SolarCycleResolution = {
  phase: SolarPhase;
  theme: AppearanceTheme;
  mode: ResolvedAppearanceMode;
  label: string;
  nextTransitionAt: Date;
  blend: SolarCycleBlend | null;
};

function dateFromLocalMinutes(day: Date, localMinutes: number): Date {
  const result = new Date(day);
  result.setHours(0, 0, 0, 0);
  result.setMinutes(localMinutes);
  return result;
}

function getLocalMinutes(date: Date): number {
  return date.getHours() * 60 + date.getMinutes() + date.getSeconds() / 60;
}

function clamp01(value: number): number {
  return Math.min(Math.max(value, 0), 1);
}

function isMinuteWithinWindow(minute: number, start: number, end: number): boolean {
  if (start <= end) {
    return minute >= start && minute < end;
  }

  return minute >= start || minute < end;
}

function getClockPhase(minute: number): SolarPhase {
  for (const window of CLOCK_PHASE_WINDOWS) {
    if (isMinuteWithinWindow(minute, window.startMinutes, window.endMinutes)) {
      return window.phase;
    }
  }

  return "night";
}

function getTransitionAtForDay(now: Date, atMinutes: number): Date {
  return dateFromLocalMinutes(now, atMinutes);
}

function getUpcomingTransitionAt(now: Date, atMinutes: number): Date {
  const today = getTransitionAtForDay(now, atMinutes);
  if (today.getTime() > now.getTime()) {
    return today;
  }

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow;
}

function getBlendWindowStart(transitionAt: Date): Date {
  return new Date(transitionAt.getTime() - SOLAR_CYCLE_BLEND_HALF_MINUTES * MS_PER_MINUTE);
}

function getBlendWindowEnd(transitionAt: Date): Date {
  return new Date(transitionAt.getTime() + SOLAR_CYCLE_BLEND_HALF_MINUTES * MS_PER_MINUTE);
}

function toBlendEndpoint(phase: SolarPhase): SolarCycleBlendEndpoint {
  const config = PHASE_BY_ID[phase];
  return {
    phase,
    theme: config.theme,
    mode: config.mode,
  };
}

export function findActiveSolarCycleBlend(now: Date): SolarCycleBlend | null {
  const nowMs = now.getTime();
  const blendDurationMs = SOLAR_CYCLE_BLEND_DURATION_MINUTES * MS_PER_MINUTE;

  for (const transition of PHASE_TRANSITIONS) {
    const transitionAt = getTransitionAtForDay(now, transition.atMinutes);
    const windowStart = getBlendWindowStart(transitionAt);
    const windowEnd = getBlendWindowEnd(transitionAt);

    if (nowMs < windowStart.getTime() || nowMs >= windowEnd.getTime()) {
      continue;
    }

    const progress = clamp01((nowMs - windowStart.getTime()) / blendDurationMs);

    return {
      from: toBlendEndpoint(transition.from),
      to: toBlendEndpoint(transition.to),
      progress,
    };
  }

  return null;
}

function getNextClockTransitionAt(now: Date, phase: SolarPhase): Date {
  const minute = getLocalMinutes(now);
  const preDawnStart = CLOCK_PHASE_WINDOWS[0].startMinutes;

  if (phase === "night") {
    if (minute < preDawnStart) {
      return dateFromLocalMinutes(now, preDawnStart);
    }

    const nextPreDawn = dateFromLocalMinutes(now, preDawnStart);
    nextPreDawn.setDate(nextPreDawn.getDate() + 1);
    return nextPreDawn;
  }

  const currentWindow = CLOCK_PHASE_WINDOWS.find((window) => window.phase === phase);
  if (!currentWindow) {
    return new Date(now.getTime() + 60 * MS_PER_MINUTE);
  }

  return dateFromLocalMinutes(now, currentWindow.endMinutes);
}

function getNextSolarCycleUpdateAt(
  now: Date,
  phase: SolarPhase,
  blend: SolarCycleBlend | null
): Date {
  if (blend && blend.progress > 0 && blend.progress < 1) {
    for (const transition of PHASE_TRANSITIONS) {
      const transitionAt = getTransitionAtForDay(now, transition.atMinutes);
      const windowEnd = getBlendWindowEnd(transitionAt);
      if (now.getTime() < windowEnd.getTime()) {
        return windowEnd;
      }
    }
  }

  const nowMs = now.getTime();
  let nextBlendStart: Date | null = null;

  for (const transition of PHASE_TRANSITIONS) {
    const transitionAt = getUpcomingTransitionAt(now, transition.atMinutes);
    const candidate = getBlendWindowStart(transitionAt);
    if (candidate.getTime() > nowMs) {
      if (!nextBlendStart || candidate.getTime() < nextBlendStart.getTime()) {
        nextBlendStart = candidate;
      }
    }
  }

  const phaseBoundary = getNextClockTransitionAt(now, phase);
  if (nextBlendStart && nextBlendStart.getTime() < phaseBoundary.getTime()) {
    return nextBlendStart;
  }

  return phaseBoundary;
}

export function resolveSolarCycleMode(
  resolution: Pick<SolarCycleResolution, "mode" | "blend">
): ResolvedAppearanceMode {
  if (!resolution.blend || resolution.blend.from.mode === resolution.blend.to.mode) {
    return resolution.mode;
  }

  return resolution.blend.progress >= 0.5 ? resolution.blend.to.mode : resolution.blend.from.mode;
}

export function resolveSolarCycleAppearance(now: Date): SolarCycleResolution {
  const blend = findActiveSolarCycleBlend(now);
  const phase = getClockPhase(getLocalMinutes(now));
  const config = PHASE_BY_ID[phase];

  return {
    phase,
    theme: config.theme,
    mode: config.mode,
    label: config.label,
    nextTransitionAt: getNextSolarCycleUpdateAt(now, phase, blend),
    blend,
  };
}

/** Minimal local-time resolver for pre-React bootstrap in index.html. */
export function resolveSolarCycleBootstrap(
  hours: number,
  minutes: number
): Pick<SolarCycleResolution, "phase" | "theme" | "mode"> {
  const config = PHASE_BY_ID[getClockPhase(hours * 60 + minutes)];
  return {
    phase: config.phase,
    theme: config.theme,
    mode: config.mode,
  };
}

export function getSolarPhaseConfig(phase: SolarPhase): SolarCyclePhaseConfig {
  return PHASE_BY_ID[phase];
}
